/**
 * Table is an immutable object that is intended for use in Doc
 * or wherever you want to store a value by key and disallow mutation.
 */
export type Table<T> = { readonly [id: string]: T };

/**
 * RWTable is a mutable object that is intended for use in reducers
 * and other places where you want to mutate a table. You should not
 * use it on Doc.
 */
export type RWTable<T> = { [id: string]: T };

export function tableRemove<T>(table: Table<T>, key: string): Table<T> {
  const tableCopy = { ...table };
  delete tableCopy[key];

  return tableCopy;
}

export function tableSet<T>(table: Table<T>, key: string, value: T): Table<T> {
  const tableCopy = { ...table };
  tableCopy[key] = value;

  return tableCopy;
}

/**
 * For each key K in type T ([K in keyof T]), it checks if T[K] extends Table<unknown>.
 * If it does, the type for key K will be K; otherwise, it will be never.
 *
 * The mapped type created in step 1 is indexed by [keyof T] which creates a union of all the
 * non-never values (i.e., all keys K for which T[K] extends Table<unknown>).
 *
 * The resulting type is a union of strings representing the names of all fields in T that are
 * of the Table<T> type.
 */
export type TableFieldNames<T> = {
  [K in keyof T]: T[K] extends Table<unknown> ? K : never;
}[keyof T];

/**
 * Merges the properties of b into a, overwriting any existing properties in a.
 */
export function tableMerge<T>(a: RWTable<T>, b: Table<T>, selector: (aVal: T, bVal: T) => T): void {
  for (const key in b) {
    a[key] = selector(a[key], b[key]);
  }
}
